import reactUpdate from 'immutability-helper';
import {useMemo, useEffect} from 'react';
import isEqual from 'react-fast-compare';

import {
  buildReactionFromComponents,
  BuildReactionFromComponentsResponse,
  ReactionComponentInput,
} from 'chemistry/ChemistryDAO';
import {ChemistryReactionRole} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  getColumnIndices,
  getMoleculeIdFromEvaluatedCell,
  getRoleFromEvaluatedCell,
} from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableHelpers';
import {
  StoichiometryStructuredTable,
  StoichiometryTableRow,
  SyncedReactionComponent,
  SyncedReactionComponents,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {NoteLinkedFile, FromServerNoteLinkedFile} from 'notebook/util/NoteUtilConstants';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';

function serverNoteLinkedFileToClientNoteLinkedFile(
  serverNoteLinkedFile: FromServerNoteLinkedFile
): NoteLinkedFile {
  // Use the server-assigned id as the client cid so the patcher can find the
  // existing NoteLinkedFile on save without insecure creation via the patcher path.
  const {id, ...rest} = serverNoteLinkedFile;
  return {...rest, cid: id};
}

// Backward compatibility: persisted tables created before CHEMISTRY_DEFER_MOLECULE_CREATION
// store syncedComponents as plain string arrays (molecule IDs). Normalize to tagged union format
// so isEqual comparisons work correctly against the new format produced by extractCurrentTableComponents.
// Once the flag is fully rolled out, remove this normalization. Tracked in BNCH-152017.
function normalizeSyncedComponents(
  components: SyncedReactionComponents | undefined
): SyncedReactionComponents | undefined {
  if (components == null) {
    return undefined;
  }
  return {
    [ChemistryReactionRole.REACTANT]: components[ChemistryReactionRole.REACTANT].map((c) =>
      typeof c === 'string' ? {type: 'molecule' as const, id: c} : c
    ),
    [ChemistryReactionRole.PRODUCT]: components[ChemistryReactionRole.PRODUCT].map((c) =>
      typeof c === 'string' ? {type: 'molecule' as const, id: c} : c
    ),
  };
}

function extractCurrentTableComponents(
  computedTable: EvaluatedTable,
  columns: Array<StructuredTableColumn>,
  rows: Array<StoichiometryTableRow>
): SyncedReactionComponents {
  const {compoundColumnIndex, roleColumnIndex} = getColumnIndices(columns);
  const components: SyncedReactionComponents = {
    [ChemistryReactionRole.REACTANT]: [],
    [ChemistryReactionRole.PRODUCT]: [],
  };

  for (let i = 0; i < computedTable.length; i++) {
    const evaluatedRow = computedTable[i];
    const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
    const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);

    if (role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT) {
      if (moleculeId) {
        components[role].push({type: 'molecule', id: moleculeId});
      } else {
        const smiles = rows[i]?.metadata?.canonicalSmiles;
        if (smiles) {
          components[role].push({type: 'smiles', smiles});
        }
      }
    }
  }

  return components;
}

function toApiComponent(component: SyncedReactionComponent | string): ReactionComponentInput {
  if (typeof component === 'string') {
    return {type: 'molecule', moleculeId: component};
  }
  return component.type === 'molecule'
    ? {type: 'molecule', moleculeId: component.id}
    : {type: 'smiles', smiles: component.smiles};
}

function buildUpdatedReactionDiagram(
  components: SyncedReactionComponents
): Promise<BuildReactionFromComponentsResponse> {
  return buildReactionFromComponents(
    components[ChemistryReactionRole.REACTANT].map((c) => toApiComponent(c)),
    components[ChemistryReactionRole.PRODUCT].map((c) => toApiComponent(c))
  );
}

/**
 * Syncs reaction components from a stoichiometry table to a reaction scheme file.
 *
 * Monitors reactant and product molecules in the table and automatically updates
 * the reaction scheme when components change. Calls the chemistry service to build
 * a new reaction diagram and invokes `onUpdate` with both the updated NoteLinkedFile
 * (carrying the new external file) and the updated table (with refreshed syncedComponents).
 *
 * @param structuredTable - The stoichiometry table containing reaction components
 * @param computedTable - The evaluated (computed) version of the table rows
 * @param reactionSchemeFile - The NoteLinkedFile that stores the reaction scheme drawing
 * @param onUpdate - Callback invoked with (updatedFile, updatedTable) when a new diagram is generated
 * @returns true if a diagram update is in progress, false otherwise
 */
export default function useSyncReactionComponentsFromTable(
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable,
  reactionSchemeFile: NoteLinkedFile,
  onUpdate: (updatedFile: NoteLinkedFile, updatedTable: StoichiometryStructuredTable) => void
): boolean {
  const diagramReactionComponents = useMemo(
    () => normalizeSyncedComponents(structuredTable.metadata.syncedComponents),
    [structuredTable.metadata.syncedComponents]
  );
  const tableReactionComponents = useMemo(
    () => extractCurrentTableComponents(computedTable, structuredTable.columns, structuredTable.rows),
    [computedTable, structuredTable.columns, structuredTable.rows]
  );
  const {currentState: reactionDiagramUpdateResponse, makeCall: callBuildUpdatedReactionDiagram} =
    useAsyncState({makeCall: buildUpdatedReactionDiagram, keepOnlyLatest: true, includeInput: true});

  useEffect(() => {
    if (
      diagramReactionComponents == null ||
      // Use deep equality check since the tableReactionComponents array is not completely stable.
      !isEqual(tableReactionComponents, diagramReactionComponents)
    ) {
      callBuildUpdatedReactionDiagram(tableReactionComponents);
    }
  }, [tableReactionComponents, diagramReactionComponents, callBuildUpdatedReactionDiagram]);

  const updatedReactionDiagramExternalFile =
    reactionDiagramUpdateResponse.status === AsyncStatus.SUCCESS
      ? reactionDiagramUpdateResponse.data.externalFile
      : null;
  const updatedReactionInput =
    reactionDiagramUpdateResponse.status !== AsyncStatus.NOT_FETCHED
      ? reactionDiagramUpdateResponse.input![0]
      : null;

  const updatedReactionDiagramNoteLinkedFile =
    reactionDiagramUpdateResponse.status === AsyncStatus.SUCCESS
      ? reactionDiagramUpdateResponse.data.noteLinkedFile
      : null;

  useEffect(() => {
    if (
      updatedReactionDiagramExternalFile != null &&
      // Ensure that this is not a stale file that no longer matches because the reaction was updated via sketcher.
      isEqual(updatedReactionInput, tableReactionComponents)
    ) {
      if (updatedReactionDiagramNoteLinkedFile == null) {
        return;
      }
      if (
        reactionSchemeFile.externalFile != null &&
        updatedReactionDiagramExternalFile.id === reactionSchemeFile.externalFile.id
      ) {
        return;
      }
      const updatedFile = serverNoteLinkedFileToClientNoteLinkedFile(updatedReactionDiagramNoteLinkedFile);
      onUpdate(
        updatedFile,
        reactUpdate(structuredTable, {
          metadata: {
            $merge: {syncedComponents: tableReactionComponents},
          },
        })
      );
    }
  }, [
    updatedReactionDiagramExternalFile,
    updatedReactionDiagramNoteLinkedFile,
    structuredTable,
    tableReactionComponents,
    updatedReactionInput,
    onUpdate,
    reactionSchemeFile,
  ]);

  return reactionDiagramUpdateResponse.status === AsyncStatus.LOADING;
}
